import { List, useStyles } from '@archipro-design/aria';
import * as S from './MobileBottomMenuContent.style';
import {
    ArrowRightSLine,
    CustomCopy,
    CustomArrowToTop,
    InboxCustom,
    PencilLine,
    AddLine,
} from '@archipro-design/icons';
import { useDesignBoardPageState } from '~/modules/design-board/component/context';
import { useUploadPin } from '~/modules/design-board/hook/use-upload-pin';
import { isUserLoggedIn, useUser } from '~/modules/user';
import {
    DESIGN_BOARD_HOME_LINK,
    PIN_TYPE_PARAM_NAME,
    SHOW_COMMENT,
    SHOW_ORGANIZE,
    SHOW_SHARE_PINS,
} from '~/modules/design-board/config';
import { getDesignBoardShareLink } from '~/modules/design-board/util';
import { useNavigate } from '@remix-run/react';
import { useUrlPinTargetType } from '~/modules/design-board/hook';
import type { ImageValidationResult } from '~/modules/file/util/validate-images';
import { useTracker } from '@archipro-website/tracker';
import LoginSignUpModal from '@modules/user/component/login-signup-modal/LoginSignUpModal';
import type { SyntheticEvent } from 'react';
import { useState } from 'react';

export const MobileBoardMenu = () => {
    const { css } = useStyles();
    const {
        dispatch,
        state: { currentBoardData },
    } = useDesignBoardPageState();
    const navigate = useNavigate();
    const urlPinType = useUrlPinTargetType();
    const tracker = useTracker();

    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const [openLoginModal, setOpenLoginModal] = useState(false);

    const uploadResultRoute =
        currentBoardData?.URLSegment && urlPinType !== 'memberUpload'
            ? `${DESIGN_BOARD_HOME_LINK}/${currentBoardData?.URLSegment}?${PIN_TYPE_PARAM_NAME}=memberUpload`
            : '';

    const closeBottomMenu = () => {
        dispatch({ type: 'CloseDrawer' });
    };

    const afterChooseUploadFile = (uploadResult: ImageValidationResult[]) => {
        closeBottomMenu();
        uploadResultRoute &&
            navigate(uploadResultRoute, {
                replace: true,
                state: { uploadResult },
            });
    };

    const { onUploadClick, hiddenInput } = useUploadPin({
        mobile: true,
        callbackFn: afterChooseUploadFile,
    });

    const onClickEditBoard = () => {
        if (!isLoggedIn) setOpenLoginModal(true);
        else {
            closeBottomMenu();
            if (currentBoardData) {
                dispatch({
                    type: 'OpenEditDrawer',
                    designBoardInput: {
                        title: currentBoardData.Title || '',
                        description: currentBoardData.Content || '',
                        isPrivate: !currentBoardData.Public,
                        id: currentBoardData.ID,
                    },
                });
            }
            tracker.log('EditBoardReveal', {
                url: new URL(window.location.href),
                data: {
                    ElementID: 'designboard_detail_header_editBoardReveal',
                    Source: 'designBoard, detail, header',
                },
            });
        }
    };

    const onClickUploadPin = (e: SyntheticEvent) => {
        if (!isLoggedIn) setOpenLoginModal(true);
        else {
            onUploadClick(e);
        }
    };

    const onClickOrganise = () => {
        // todo: implement this feature later
        console.log(
            '🚀 ~ file: MobileBoardMenu.tsx:59 ~ onClickOrganise ~ onClickOrganise:'
        );
        closeBottomMenu();
    };

    const onClickComments = () => {
        // todo: implement this feature later
        console.log(
            '🚀 ~ file: MobileBoardMenu.tsx:63 ~ onClickComments ~ onClickComments:'
        );
        closeBottomMenu();
    };

    const onClickShareLink = () => {
        const shareData = {
            title: currentBoardData?.Title,
            url: getDesignBoardShareLink(currentBoardData?.URLSegment || ''),
        };

        tracker.log('Share', {
            url: new URL(shareData.url),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: shareData.title,
            },
        });

        closeBottomMenu();

        if (shareData.url && navigator.share) {
            navigator.share(shareData);
        }
    };

    const menuItems = [
        currentBoardData?.CanEdit && {
            label: 'Edit board',
            onClick: onClickEditBoard,
            iconBefore: <PencilLine className="edit-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
            disabled: !currentBoardData?.CanEdit,
        },
        currentBoardData?.Permission.CanUploadFile && {
            label: 'Upload new pin',
            onClick: onClickUploadPin,
            iconBefore: <CustomArrowToTop className="upload-icon" />,
            iconAfter: <ArrowRightSLine className="arrow-icon" />,
            disabled: !currentBoardData?.Permission.CanUploadFile,
        },
        SHOW_ORGANIZE &&
            currentBoardData?.CanEdit && {
                label: 'Organise',
                onClick: onClickOrganise,
                iconBefore: <CustomCopy className="copy-icon" />,
                iconAfter: <ArrowRightSLine className="arrow-icon" />,
            },
        SHOW_COMMENT &&
            currentBoardData?.Public && {
                label: 'Comments',
                onClick: onClickComments,
                iconBefore: <InboxCustom className="comment-icon" />,
                iconAfter: <ArrowRightSLine className="arrow-icon" />,
            },
        SHOW_SHARE_PINS &&
            currentBoardData?.Public && {
                label: 'Share',
                onClick: onClickShareLink,
                iconBefore: <AddLine className="share-icon" />,
                iconAfter: <ArrowRightSLine className="arrow-icon" />,
            },
    ];

    return (
        <>
            <div className={css(S.BottomMenuWrapper)}>
                <List items={menuItems} />
                {hiddenInput}
            </div>
            <LoginSignUpModal
                login={{
                    onCancel: () => setOpenLoginModal(false),
                    open: openLoginModal,
                    flow: null,
                    authSource: 'designBoard',
                    onAuthSuccess: () => setOpenLoginModal(false),
                }}
                forgotPassword={{}}
            />
        </>
    );
};
